import { useEffect, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/nextjs';
import { FieldErrors, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  CoachSportStatusesEnum,
  SportsEnum,
  useUpdateCoachSocialsMutation,
} from 'types/generated/client';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonText } from 'components/Button';
import { getUpdatedCoachSports } from 'components/Coach/utils';
import InputField from 'components/coaches-builder/InputField';
import { InputType } from 'components/coaches-builder/InputField/InputField';
import ProfileItem from 'components/coaches-builder/ProfileItem';
import MultiSelect from 'components/forms/MultiSelect';
import RadioList from 'components/forms/RadioList';
import Select from 'components/forms/Select';
import Switcher from 'components/forms/Switcher';
import Modal from 'components/modals/Modal';
import { TagsAndLinksSchema } from './schemas';
import { FormModalModeEnum, FormModalProps } from '../types';

interface TagsAndLinksFormModalProps extends FormModalProps {}

type TagsAndLinksSchemaType = z.infer<typeof TagsAndLinksSchema>;

const TagsAndLinksFormModal: React.FC<TagsAndLinksFormModalProps> = ({
  isOpen,
  handleClose,
  coachData,
  mode,
}) => {
  const {
    register,
    control,
    setValue,
    reset,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm<TagsAndLinksSchemaType>({
    resolver: zodResolver(TagsAndLinksSchema),
    defaultValues: {
      sports: [],
      defaultSport: '',
    },
  });

  const [updateCoachSocials, { loading: updateCoachSocialsLoading }] =
    useUpdateCoachSocialsMutation();

  const viewer = useViewer();

  const selectedSports = watch('sports');

  const defaultSelectedSport = watch('defaultSport');

  const sportsRef = useRef<HTMLDivElement>(null);
  const defaultSportRef = useRef<HTMLDivElement>(null);

  const onSubmit = async (data: TagsAndLinksSchemaType) => {
    const updatedCoachSports = getUpdatedCoachSports({
      userId: viewer.userId as string,
      previousSports: coachData!.coachSports.map((s) => s.sport as SportsEnum),
      newSports: data.sports,
    });

    try {
      await updateCoachSocials({
        variables: {
          defaultSport: data.defaultSport as SportsEnum,
          sports: updatedCoachSports,
          xUrl: data.xUrl,
          linkedinUrl: data.linkedInUrl,
          facebookUrl: data.facebookUrl,
          youtubeUrl: data.youtubeUrl,
          websiteUrl: data.websiteUrl,
          instagramUrl: data.instagramUrl,
          userId: viewer.userId,
        },
      });
      handleClose(true); // recalling coach api
    } catch (err) {
      captureException(err);
    }
  };

  const onErrors = (errors: FieldErrors<TagsAndLinksSchemaType>) => {
    if (errors.sports) {
      sportsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (errors.defaultSport) {
      defaultSportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
  };

  const sportsOptions = [
    { label: 'Padel', value: SportsEnum.Padel },
    { label: 'Tennis', value: SportsEnum.Tennis },
    { label: 'Pickleball', value: SportsEnum.Pickleball },
  ];

  const defaultSportsOptions = sportsOptions.filter((sportsOption) =>
    selectedSports.includes(sportsOption.value),
  );

  useEffect(() => {
    // if (mode === FormModalModeEnum.Add) {
    //   reset();
    //   return;
    // }

    if (!coachData) {
      return;
    }
    setValue('defaultSport', coachData?.defaultSport || '');
    setValue('xUrl', coachData?.xUrl || '');
    setValue('facebookUrl', coachData.facebookUrl || '');
    setValue('linkedInUrl', coachData.linkedinUrl || '');
    setValue('instagramUrl', coachData.instagramUrl || '');
    setValue('youtubeUrl', coachData.youtubeUrl || '');
    setValue('websiteUrl', coachData.websiteUrl || '');
    setValue('defaultSport', coachData.defaultSport || '');
    const preSelectedSports = coachData.coachSports.map(
      (coachSport) => coachSport.sport as SportsEnum,
    );
    if (preSelectedSports.length) {
      setValue('sports', preSelectedSports);
    }
  }, [coachData, reset, setValue]);

  useEffect(() => {
    const isAmongOptions = selectedSports.some(
      (selectedSport) => defaultSelectedSport === selectedSport,
    );
    if (!isAmongOptions) {
      setValue('defaultSport', '');
    }
  }, [selectedSports, defaultSelectedSport]);

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} classNameMaxWidth="max-w-[80vw]">
      <div className="flex items-center justify-between border-b px-8 py-4">
        <h2 className="typography-product-heading-desktop">Tags and Links</h2>
        <ButtonText onClick={() => handleClose()}>
          <CloseIcon className="h-4 w-4" />
        </ButtonText>
      </div>
      <div className="flex flex-col px-4 md:px-10">
        <ProfileItem
          label="Sports"
          description="Sports you are coaching"
          component={
            <div ref={sportsRef}>
              <MultiSelect
                errors={errors}
                placeholder="Select sports"
                control={control}
                name="sports"
                options={sportsOptions}
              />
            </div>
          }
        />

        <ProfileItem
          label="Default sport"
          description="Default preference for your selected sports"
          component={
            <div className="mb-4">
              {selectedSports.length > 0 ? (
                <div className="flex w-full flex-col" ref={defaultSportRef}>
                  <RadioList
                    listHeader=""
                    control={control}
                    errors={errors}
                    name={`defaultSport`}
                    options={defaultSportsOptions}
                    isHideOther
                    groupClassName="flex items-center gap-6"
                  />
                  {errors.defaultSport && (
                    <p className="mt-2  text-xs text-color-error">{errors.defaultSport.message}</p>
                  )}
                </div>
              ) : (
                <span className=" text-size-informative-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                  Select sports for options
                </span>
              )}
            </div>
          }
        />
        <ProfileItem
          label="Social media links"
          description="Add links to your social media"
          component={
            <div className="flex flex-col gap-8">
              <InputField
                errors={errors}
                placeholder="https://"
                name="websiteUrl"
                register={register}
                inputType={InputType.LabelInput}
                fieldLabel={'Personal Website'}
              />
              <InputField
                errors={errors}
                placeholder="https://"
                name="instagramUrl"
                fieldLabel={'Instagram'}
                register={register}
                inputType={InputType.LabelInput}
              />
              <InputField
                errors={errors}
                placeholder="https://"
                name="youtubeUrl"
                fieldLabel={'Youtube'}
                register={register}
                inputType={InputType.LabelInput}
              />
              <InputField
                errors={errors}
                placeholder="https://"
                name="facebookUrl"
                fieldLabel={'Facebook'}
                register={register}
                inputType={InputType.LabelInput}
              />
              <InputField
                errors={errors}
                placeholder="https://"
                name="linkedInUrl"
                fieldLabel={'LinkedIn'}
                register={register}
                inputType={InputType.LabelInput}
              />
              <InputField
                errors={errors}
                placeholder="https://"
                name="xUrl"
                fieldLabel={'X'}
                register={register}
                inputType={InputType.LabelInput}
              />
            </div>
          }
        />
      </div>
      <div className="flex justify-end border-t px-8 py-4">
        <Button
          disabled={updateCoachSocialsLoading}
          variant="primary"
          onClick={handleSubmit(onSubmit, onErrors)}
          className="w-fit"
          size="lg"
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default TagsAndLinksFormModal;
